import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { JSX } from 'react';
import { api, ApiError } from '../api/client.js';
import type { SessionDto } from '../api/types.js';

interface AuthState {
  session: SessionDto | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  signIn: (email: string, password: string, totp?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Sessão do painel. Requisitos do plano (§5.1): admin exige conta autorizada e
 * MFA; a sessão é cookie HttpOnly/Secure — nada de token em storage acessível
 * por JavaScript. A autorização real é sempre revalidada pelo servidor.
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<SessionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const current = await api.get<SessionDto>('/v1/auth/session');
      setSession(current);
      setError(null);
    } catch (caught) {
      setSession(null);
      if (caught instanceof ApiError && !['UNAUTHENTICATED', 'SESSION_EXPIRED'].includes(caught.code)) {
        setError(caught.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string, totp?: string) => {
      await api.post('/v1/auth/login', { email, password, ...(totp ? { code: totp } : {}) });
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/v1/auth/logout');
    } finally {
      setSession(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ session, loading, error, refresh, signIn, signOut }),
    [session, loading, error, refresh, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de AuthProvider');
  return context;
}

/** Papéis de plataforma são checados no cliente apenas para esconder a UI. */
export function hasPlatformRole(session: SessionDto | null, role: string): boolean {
  return session?.platformRoles.includes(role) ?? false;
}